import { useEffect, useRef, useState } from "react";
import { api, fileUrl, fmtMoney, API } from "@/lib/api";
import { Plus, Pencil, Trash2, Upload, X, Download, FileSpreadsheet, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ManifestEditor from "@/components/admin/ManifestEditor";
import { HelpTip } from "@/components/HelpTip";

const EMPTY = {
  name: "",
  sku: "",
  description: "",
  category: "Furniture",
  price: 0,
  compare_at_price: null,
  stock: 0,
  is_pallet: false,
  is_active: true,
  images: [],
  tags: [],
  low_stock_threshold: 5,
  colors: [],
  bulk_deal: null,
  fulfillment_type: "in_stock",
  lead_time_days: null,
  condition: null,
  condition_notes: "",
};

const CATEGORIES = [
  "Recliners",
  "Chairs",
  "Furniture",
  "Mattresses",
  "Scooters",
  "E-Bikes",
  "Appliances",
  "Landscaping",
  "Closeout & Overstock Pallets",
  "Deals Under $20",
  "Uncategorized",
];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | EMPTY | product
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [showNeedsReviewOnly, setShowNeedsReviewOnly] = useState(false);
  const [showPalletPicksOnly, setShowPalletPicksOnly] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [colorInput, setColorInput] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const bulkInputRef = useRef(null);

  // Phase 14a — call Claude Sonnet 5 to generate a product description
  const generateDescription = async () => {
    if (!editing?.id) {
      toast.error("Save the product first, then generate a description.");
      return;
    }
    setGenLoading(true);
    try {
      const { data } = await api.post(`/admin/products/${editing.id}/generate-description`);
      setEditing((prev) => ({
        ...prev,
        description_draft: data.text,
        description_draft_insufficient: data.insufficient,
      }));
      if (data.insufficient) {
        toast.error("Claude says: not enough product data. Add category, condition, or price.");
      } else {
        toast.success("Draft generated — review and click 'Apply to description'.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Generation failed";
      toast.error(String(msg));
    } finally {
      setGenLoading(false);
    }
  };

  const applyDraftToDescription = () => {
    setEditing((prev) => ({
      ...prev,
      description: prev.description_draft || prev.description || "",
    }));
    toast.success("Draft applied. Click Save to publish.");
  };

  const addColor = () => {
    const name = colorInput.trim();
    if (!name) return;
    setEditing((prev) => {
      const colors = prev.colors || [];
      if (colors.some((c) => c.name.toLowerCase() === name.toLowerCase())) return prev;
      return { ...prev, colors: [...colors, { name, available: true }] };
    });
    setColorInput("");
  };

  const load = () => api.get("/products/admin/all").then((r) => setProducts(r.data.items));

  const autoPickGold = async () => {
    if (!window.confirm("Auto-tag the top 10 sellers & best-margin in-stock items as ★ GOLD DEAL?\n\nPrevious auto-picks are cleared — gold tags you set by hand are kept.")) return;
    try {
      const { data } = await api.post("/products/admin/gold-auto-pick?limit=10");
      toast.success(`★ Tagged ${data.tagged} Gold Deal${data.tagged === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error("Auto-pick failed");
    }
  };

  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/product-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((prev) => ({ ...prev, images: [...(prev.images || []), data.path] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setEditing((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx),
    }));
  };

  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    if (saving) return;
    if (editing.is_active && !(editing.images || []).length) {
      toast.error("Add at least one photo before publishing — or uncheck \"Active (visible)\" to save it hidden.");
      return;
    }
    setSaving(true);
    try {
      const minQty = parseInt(editing.bulk_deal?.min_qty) || 0;
      const pctOff = parseFloat(editing.bulk_deal?.percent_off) || 0;
      const payload = {
        ...editing,
        price: parseFloat(editing.price) || 0,
        compare_at_price: editing.compare_at_price ? parseFloat(editing.compare_at_price) : null,
        cost: editing.cost !== "" && editing.cost != null ? parseFloat(editing.cost) : null,
        is_suite18: !!editing.is_suite18,
        gold_tag: !!editing.gold_tag,
        stock: parseInt(editing.stock) || 0,
        low_stock_threshold: parseInt(editing.low_stock_threshold) || 5,
        colors: (editing.colors || []).map((c) => ({ name: c.name, available: c.available !== false })),
        bulk_deal: minQty >= 2 && pctOff > 0 ? { min_qty: minQty, percent_off: pctOff } : null,
        fulfillment_type: editing.fulfillment_type || "in_stock",
        lead_time_days: editing.fulfillment_type === "order_in" ? (parseInt(editing.lead_time_days) || 7) : null,
      };
      if (editing.id) {
        await api.patch(`/products/admin/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products/admin", payload);
        toast.success("Product created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/products/admin/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const filtered = (() => {
    let list = products;
    if (showNeedsReviewOnly) list = list.filter((p) => p.needs_review);
    if (showPalletPicksOnly) list = list.filter((p) => p.custom_pallet_eligible);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        [p.name, p.sku, p.category].some((v) => v?.toLowerCase().includes(q))
      );
    }
    return list;
  })();

  const needsReviewCount = products.filter((p) => p.needs_review).length;
  const palletPickCount = products.filter((p) => p.custom_pallet_eligible).length;

  const togglePalletPick = async (p, checked) => {
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, custom_pallet_eligible: checked } : x)));
    try {
      await api.patch(`/products/admin/${p.id}`, { custom_pallet_eligible: checked });
      toast.success(checked ? `"${p.name}" added to Custom Pallet picker` : `"${p.name}" removed from picker`);
    } catch {
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, custom_pallet_eligible: !checked } : x)));
      toast.error("Couldn't update — try again");
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("ipv_token");
    const resp = await fetch(`${API}/products/admin/bulk/template`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) {
      toast.error("Failed to download template");
      return;
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ipvalue-bulk-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("ipv_token");
    const resp = await fetch(`${API}/products/admin/bulk/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) {
      toast.error("Failed to export inventory");
      return;
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ipvalue-inventory-export.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Inventory CSV downloaded");
  };

  const onBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkImporting(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/admin/bulk/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(data);
      const { summary } = data;
      if (summary.errors === 0) {
        toast.success(`Imported ${summary.created} new, updated ${summary.updated}`);
      } else {
        toast.warning(
          `Imported ${summary.created} new, updated ${summary.updated}, ${summary.errors} errors`
        );
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkImporting(false);
      if (bulkInputRef.current) bulkInputRef.current.value = "";
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="ipv-eyebrow text-[#0A1C40]">Catalog</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mt-2">Products</h1>
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          {needsReviewCount > 0 && (
            <button
              onClick={() => setShowNeedsReviewOnly((v) => !v)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${
                showNeedsReviewOnly
                  ? "bg-[#DC2626] text-white border-[#DC2626]"
                  : "bg-white text-[#DC2626] border-[#DC2626] hover:bg-[#FEF2F2]"
              }`}
              data-testid="needs-review-filter"
              title="Filter to WhatsApp uploads where AI couldn't extract details"
            >
              ⚠ Needs review ({needsReviewCount})
            </button>
          )}
          <button
            onClick={() => setShowPalletPicksOnly((v) => !v)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${
              showPalletPicksOnly
                ? "bg-[#0A1C40] text-[#FFC220] border-[#0A1C40]"
                : "bg-white text-[#0A1C40] border-[#0A1C40] hover:bg-blue-50"
            }`}
            data-testid="pallet-picks-filter"
            title="Show only items flagged for the Custom Pallet builder — use the checkboxes in the table to bulk-flag"
          >
            📦 Pallet picks ({palletPickCount})
          </button>
          <input
            className="input-base"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-products-search"
          />
          <button onClick={autoPickGold} className="btn-outline inline-flex items-center gap-2" data-testid="gold-auto-pick-btn" title="Auto-tag the top sellers & best-margin in-stock items as ★ GOLD DEAL">
            ★ Auto-pick Gold Deals
          </button>
          <button onClick={exportCsv} className="btn-outline inline-flex items-center gap-2" data-testid="admin-export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={() => setBulkOpen(true)} className="btn-outline inline-flex items-center gap-2" data-testid="admin-bulk-import-btn">
            <FileSpreadsheet className="w-4 h-4" /> Bulk import
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary inline-flex items-center gap-2" data-testid="admin-add-product-btn">
            <Plus className="w-4 h-4" /> Add product
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 mt-8 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-gray-200 text-left">
            <tr className="ipv-eyebrow">
              <th className="p-3 w-14"></th>
              <th className="p-3">Name</th>
              <th className="p-3">Category</th>
              <th className="p-3">SKU</th>
              <th className="p-3 text-right">Price</th>
              <th className="p-3 text-right">Stock</th>
              <th className="p-3 text-center whitespace-nowrap">
                Pallet pick{" "}
                <HelpTip
                  testId="help-pallet-pick-col"
                  text="Tick to show this item in the Build-My-Pallet picker. Only flag items physically in your Custom Pallet Selection warehouse area. Tick many rows in a row to bulk-flag — each click saves instantly."
                />
              </th>
              <th className="p-3 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((p) => (
              <tr
                key={p.id}
                data-testid={`admin-row-${p.id}`}
                className={p.needs_review ? "bg-[#FEF2F2]" : ""}
              >
                <td className="p-2">
                  <div className="w-10 h-10 bg-gray-100 overflow-hidden">
                    {p.images?.[0] && <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                </td>
                <td className="p-3 max-w-[280px]">
                  <div className="font-bold truncate flex items-center gap-2">
                    {p.needs_review && (
                      <span
                        className="inline-flex items-center justify-center bg-[#DC2626] text-white text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0"
                        title="AI parse failed — please review and edit"
                        data-testid={`needs-review-flag-${p.id}`}
                      >
                        ⚠ REVIEW
                      </span>
                    )}
                    <span className="truncate">{p.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{p.slug}</div>
                </td>
                <td className="p-3 text-gray-700">{p.category}</td>
                <td className="p-3 text-gray-500 text-xs">{p.sku || "—"}</td>
                <td className="p-3 text-right font-bold">{fmtMoney(p.price)}</td>
                <td className={`p-3 text-right font-bold ${p.stock <= 0 ? "text-[#DC2626]" : p.stock <= (p.low_stock_threshold || 5) ? "text-yellow-700" : ""}`}>
                  {p.stock}
                </td>
                <td className="p-3 text-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#0A1C40] cursor-pointer"
                    checked={!!p.custom_pallet_eligible}
                    onChange={(e) => togglePalletPick(p, e.target.checked)}
                    disabled={p.is_pallet}
                    title={p.is_pallet ? "Pallets can't go inside a custom pallet" : "Show in the Custom Pallet builder"}
                    data-testid={`pallet-pick-toggle-${p.id}`}
                  />
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setEditing({ ...p })} className="p-2 hover:bg-gray-100" data-testid={`admin-edit-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => onDelete(p)} className="p-2 hover:bg-red-50 text-[#DC2626]" data-testid={`admin-delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {bulkOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-bulk-modal">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="font-display font-bold text-xl flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" /> Bulk CSV import
              </div>
              <button onClick={() => { setBulkOpen(false); setBulkResult(null); }} className="p-2"><X /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <div className="ipv-eyebrow mb-2">How it works</div>
                <ol className="text-sm space-y-1.5 text-gray-700 list-decimal list-inside">
                  <li>Download the CSV template below</li>
                  <li>Fill in your products (one row per item). Required: <b>name, category, price</b></li>
                  <li>Put image URLs in <b>image_urls</b> column (semicolon-separated for multiple)</li>
                  <li>Drop the saved CSV here — rows with matching <b>SKU</b> update existing products; new SKUs are created</li>
                </ol>
              </div>

              <div className="bg-[#FAFAFA] border border-gray-200 p-4 text-xs space-y-1">
                <div className="font-bold uppercase tracking-wider text-gray-500 mb-2">Columns</div>
                <div><b>name</b>* · product name</div>
                <div><b>category</b>* · Recliners, Chairs, Furniture, Mattresses, Scooters, E-Bikes, Appliances, Landscaping, Closeout & Overstock Pallets, Deals Under $20</div>
                <div><b>price</b>* · numeric (e.g., 599.00)</div>
                <div><b>sku</b> · optional but recommended (used for updates)</div>
                <div><b>compare_at_price</b> · MSRP/strikethrough price</div>
                <div><b>stock</b> · integer</div>
                <div><b>description</b> · plain text</div>
                <div><b>image_urls</b> · URLs separated by semicolons (<code>;</code>)</div>
                <div><b>tags</b> · semicolon-separated</div>
                <div><b>is_pallet</b> · true/false (true = wholesale badge)</div>
                <div><b>is_active</b> · true/false (true = show on storefront)</div>
                <div><b>low_stock_threshold</b> · integer (default 5)</div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button onClick={downloadTemplate} className="btn-outline inline-flex items-center gap-2" data-testid="download-template-btn">
                  <Download className="w-4 h-4" /> Download template CSV
                </button>
                <label className="btn-primary inline-flex items-center gap-2 cursor-pointer" data-testid="bulk-upload-label">
                  <Upload className="w-4 h-4" />
                  {bulkImporting ? "Importing…" : "Upload CSV"}
                  <input
                    ref={bulkInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={onBulkImport}
                    disabled={bulkImporting}
                    data-testid="bulk-upload-input"
                  />
                </label>
              </div>

              {bulkResult && (
                <div className="border border-gray-200" data-testid="bulk-result">
                  <div className="p-4 border-b border-gray-200 grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="font-display font-black text-2xl text-green-700">{bulkResult.summary.created}</div>
                      <div className="ipv-eyebrow text-gray-500 mt-1">Created</div>
                    </div>
                    <div>
                      <div className="font-display font-black text-2xl text-[#0A1C40]">{bulkResult.summary.updated}</div>
                      <div className="ipv-eyebrow text-gray-500 mt-1">Updated</div>
                    </div>
                    <div>
                      <div className={`font-display font-black text-2xl ${bulkResult.summary.errors > 0 ? "text-[#DC2626]" : "text-gray-300"}`}>{bulkResult.summary.errors}</div>
                      <div className="ipv-eyebrow text-gray-500 mt-1">Errors</div>
                    </div>
                  </div>
                  {bulkResult.errors.length > 0 && (
                    <div className="p-4 bg-red-50 text-sm max-h-40 overflow-auto">
                      {bulkResult.errors.map((e, i) => (
                        <div key={i} className="text-[#DC2626]">Row {e.row}: {e.error}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-edit-modal">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="font-display font-bold text-xl">{editing.id ? "Edit product" : "New product"}</div>
              <button onClick={() => setEditing(null)} className="p-2"><X /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="ipv-eyebrow block mb-1.5">Name</label>
                <input className="input-base" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="form-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="ipv-eyebrow block mb-1.5">SKU</label>
                  <input className="input-base" value={editing.sku || ""} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} data-testid="form-sku" />
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1.5">Category</label>
                  <select className="input-base" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="form-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="ipv-eyebrow block mb-1.5">
                    Condition <span className="text-red-600" title="Required for AI description generation">*</span>{" "}
                    <HelpTip
                      testId="help-condition"
                      text="NEW = still sealed/unopened. OPEN BOX = packaging was opened but the item is unused. AS-IS = sold with a known flaw — always add a flaw note so the customer (and your return policy) are protected."
                    />
                  </label>
                  <select className="input-base" value={editing.condition || ""} onChange={(e) => setEditing({ ...editing, condition: e.target.value || null })} data-testid="form-condition">
                    <option value="">— not set —</option>
                    <option value="NEW">NEW (unopened)</option>
                    <option value="OPEN BOX">OPEN BOX (packaging opened)</option>
                    <option value="AS-IS">AS-IS (sold as-is)</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="ipv-eyebrow block mb-1.5">
                    Flaw / condition notes {editing.condition === "AS-IS" && <span className="text-amber-700 text-[10px]">(recommended for AS-IS)</span>}
                  </label>
                  <input className="input-base" placeholder={editing.condition === "AS-IS" ? "e.g. scratch on left arm" : "Only used when Condition = AS-IS"} value={editing.condition_notes || ""} onChange={(e) => setEditing({ ...editing, condition_notes: e.target.value })} data-testid="form-condition-notes" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="ipv-eyebrow">Description</label>
                  <button
                    type="button"
                    onClick={generateDescription}
                    disabled={genLoading || !editing.id}
                    title={editing.id ? "Ask Claude Sonnet 5 to draft a description from the product data" : "Save the product first"}
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-[#0A1C40] text-[#FFC220] px-2.5 py-1 hover:bg-[#12244d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="generate-description-btn"
                  >
                    <Sparkles className="w-3 h-3" />
                    {genLoading ? "Generating…" : "AI Draft"}
                  </button>
                </div>
                <textarea className="input-base" rows={4} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="form-description" />
              </div>
              <div>
                <label className="ipv-eyebrow block mb-1.5">Promo video URL — MP4 video slot (optional)</label>
                <input
                  className="input-base"
                  placeholder="https://…/my-product-video.mp4 — plays on hover + popup player"
                  value={editing.video_url || ""}
                  onChange={(e) => setEditing({ ...editing, video_url: e.target.value.trim() })}
                  data-testid="form-video-url"
                />
                {editing.description_draft && (
                  <div className="mt-2 border border-amber-300 bg-amber-50 p-3" data-testid="description-draft-panel">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-amber-900 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Claude draft {editing.description_draft_insufficient && "· INSUFFICIENT DATA"}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={applyDraftToDescription}
                          disabled={editing.description_draft_insufficient}
                          className="text-[10px] font-bold uppercase tracking-wider bg-amber-900 text-white px-2.5 py-1 hover:bg-amber-950 disabled:opacity-40"
                          data-testid="apply-draft-btn"
                        >
                          Apply to description
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, description_draft: "", description_draft_insufficient: false })}
                          className="text-[10px] font-bold uppercase tracking-wider text-amber-900 border border-amber-700 px-2.5 py-1 hover:bg-amber-100"
                          data-testid="dismiss-draft-btn"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-amber-950 whitespace-pre-wrap font-serif" data-testid="description-draft-text">
                      {editing.description_draft}
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="ipv-eyebrow block mb-1.5">Price ($)</label>
                  <input type="number" step="0.01" className="input-base" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="form-price" />
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1.5">
                    Compare at{" "}
                    <HelpTip
                      testId="help-compare"
                      text="The MSRP / retail price. Customers see it crossed out next to your price with a red % OFF badge. Leave blank to hide the comparison."
                    />
                  </label>
                  <input type="number" step="0.01" className="input-base" value={editing.compare_at_price || ""} onChange={(e) => setEditing({ ...editing, compare_at_price: e.target.value })} data-testid="form-compare" />
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1.5">
                    Your cost ($){" "}
                    <HelpTip
                      testId="help-cost"
                      text="What YOU paid for this item. Private — customers and staff never see it. Powers the margin numbers in the owner-only Reports page. For pallets, use Reports → Missing costs to split a pallet cost across items."
                    />
                  </label>
                  <input type="number" step="0.01" className="input-base border-amber-300" placeholder="private" value={editing.cost ?? ""} onChange={(e) => setEditing({ ...editing, cost: e.target.value })} data-testid="form-cost" />
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1.5">Stock</label>
                  <input type="number" className="input-base" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} data-testid="form-stock" />
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1.5">Low alert</label>
                  <input type="number" className="input-base" value={editing.low_stock_threshold} onChange={(e) => setEditing({ ...editing, low_stock_threshold: e.target.value })} data-testid="form-low-threshold" />
                </div>
              </div>

              <div className="flex gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.is_pallet} onChange={(e) => setEditing({ ...editing, is_pallet: e.target.checked })} data-testid="form-is-pallet" />
                  Wholesale pallet
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="form-is-active" />
                  Active (visible)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.gold_tag} onChange={(e) => setEditing({ ...editing, gold_tag: e.target.checked })} data-testid="form-gold-tag" />
                  ★ Gold Deal{" "}
                  <HelpTip testId="help-gold-tag" text="Shows the gold '★ GOLD DEAL' ribbon on the product card and product page, with the compare-at price emphasized ('sells elsewhere for $X'). Set a Compare-at price so shoppers see the savings. Also syncs from a 'gold-tag' tag in Shopify." />
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.is_suite18} onChange={(e) => setEditing({ ...editing, is_suite18: e.target.checked })} data-testid="form-is-suite18" />
                  Suite 18 (restricted dept)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.custom_pallet_eligible} onChange={(e) => setEditing({ ...editing, custom_pallet_eligible: e.target.checked })} data-testid="form-custom-pallet-eligible" />
                  Custom Pallet Selection inventory{" "}
                  <HelpTip testId="help-custom-pallet-eligible" text="Tick this ONLY for items in the designated warehouse Custom Pallet Selection area. Only these items appear in the Build-My-Pallet picker — never showroom or retail-listed merchandise." />
                </label>
              </div>
              {editing.is_active && !(editing.images || []).length && (
                <div className="text-xs bg-amber-50 border border-amber-400 text-amber-800 px-3 py-2 font-semibold" data-testid="publish-photo-warning">
                  ⚠ No photos yet — this product can't be published. Add at least one photo below, or uncheck "Active (visible)" to save it hidden.
                </div>
              )}
              {editing.is_pallet && (editing.images || []).length > 0 && (editing.images || []).length < 3 && (
                <div className="text-xs bg-blue-50 border border-[#0A1C40]/30 text-[#0A1C40] px-3 py-2 font-semibold" data-testid="pallet-photo-nudge">
                  📸 Tip: pallets sell better with 3-4 real photos (front, sides, top of the stack). Shoppers see them as a swipeable gallery — especially important on unmanifested pallets.
                </div>
              )}

              <div>
                <label className="ipv-eyebrow block mb-2">Availability</label>
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    className="input-base !w-auto"
                    value={editing.fulfillment_type || "in_stock"}
                    onChange={(e) => setEditing({ ...editing, fulfillment_type: e.target.value })}
                    data-testid="form-fulfillment-type"
                  >
                    <option value="in_stock">✅ Pickup Today (in warehouse)</option>
                    <option value="order_in">📦 Special Order (Faire / Walmart Business)</option>
                  </select>
                  {editing.fulfillment_type === "order_in" && (
                    <div className="flex items-center gap-2 text-sm">
                      <span>Ready in ~</span>
                      <input type="number" min="1" max="60" className="input-base !w-20" placeholder="7" value={editing.lead_time_days || ""} onChange={(e) => setEditing({ ...editing, lead_time_days: e.target.value })} data-testid="form-lead-days" />
                      <span>days</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">Special Order items can be bought even with 0 stock — you order them from your supplier after the customer pays.</p>
              </div>

              <div>
                <label className="ipv-eyebrow block mb-2">Color options</label>
                <div className="flex gap-2 mb-2">
                  <input
                    className="input-base"
                    placeholder="e.g. Red — press Add"
                    value={colorInput}
                    onChange={(e) => setColorInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addColor(); } }}
                    data-testid="form-color-input"
                  />
                  <button type="button" onClick={addColor} className="btn-outline shrink-0" data-testid="form-color-add">Add</button>
                </div>
                {(editing.colors || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {editing.colors.map((c, i) => (
                      <span key={i} className={`inline-flex items-center gap-1.5 border-2 px-3 py-1.5 text-sm font-bold ${c.available !== false ? "border-green-600 text-green-800 bg-green-50" : "border-gray-300 text-gray-400 line-through bg-gray-50"}`}>
                        <button
                          type="button"
                          onClick={() => setEditing((prev) => ({ ...prev, colors: prev.colors.map((cc, ii) => ii === i ? { ...cc, available: cc.available === false } : cc) }))}
                          title="Click to toggle availability"
                          data-testid={`form-color-toggle-${i}`}
                        >
                          {c.name}
                        </button>
                        <button type="button" onClick={() => setEditing((prev) => ({ ...prev, colors: prev.colors.filter((_, ii) => ii !== i) }))} className="text-gray-400 hover:text-[#DC2626]" data-testid={`form-color-remove-${i}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">Click a color name to mark it available / not available. Customers see unavailable colors crossed out.</p>
              </div>

              <div>
                <label className="ipv-eyebrow block mb-2">
                  Quantity deal (optional){" "}
                  <HelpTip
                    testId="help-gold-deal"
                    text="The gold 'BUY N+ SAVE X%' deal. Example: Buy 3 or more, save 10% — customers who add 3+ of this item get 10% off automatically at checkout. Great for moving pallet-broken multiples fast."
                  />
                </label>
                <div className="flex items-center gap-3 text-sm">
                  <span>Buy</span>
                  <input type="number" min="2" className="input-base !w-20" placeholder="3" value={editing.bulk_deal?.min_qty || ""} onChange={(e) => setEditing({ ...editing, bulk_deal: { ...(editing.bulk_deal || {}), min_qty: e.target.value } })} data-testid="form-deal-qty" />
                  <span>or more, save</span>
                  <input type="number" min="1" max="90" className="input-base !w-20" placeholder="10" value={editing.bulk_deal?.percent_off || ""} onChange={(e) => setEditing({ ...editing, bulk_deal: { ...(editing.bulk_deal || {}), percent_off: e.target.value } })} data-testid="form-deal-pct" />
                  <span>%</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">Shows a gold deal badge on the product and applies automatically at checkout. Leave blank for no deal.</p>
              </div>

              <div>
                <label className="ipv-eyebrow block mb-2">Images</label>
                <div className="grid grid-cols-4 gap-2">
                  {(editing.images || []).map((img, i) => (
                    <div key={i} className="relative aspect-square border border-gray-200">
                      <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/70 text-white p-1 hover:bg-[#DC2626]">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className="aspect-square border border-dashed border-gray-300 flex flex-col items-center justify-center text-xs text-gray-500 cursor-pointer hover:border-[#0A1C40] hover:text-[#0A1C40]">
                    <Upload className="w-5 h-5 mb-1" />
                    {uploading ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="form-upload-input" />
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">PNG/JPG/WEBP up to 8MB. First image is primary.</p>
              </div>

              {/* Manifest editor — only for saved pallet products */}
              {editing.id && (editing.is_pallet || editing.category === "Closeout & Overstock Pallets") && (
                <ManifestEditor product={editing} />
              )}
              {editing.id && editing.category === "Adult (18+)" && (
                <div className="border-2 border-purple-300 bg-purple-50 p-3">
                  <div className="text-xs font-bold text-purple-900 mb-1">
                    Shopify checkout (adult items){" "}
                    <HelpTip testId="help-shopify" text="Adult items are paid on your Electroxury Shopify store, not Square. Sync pushes this product (name, price, photos) to Shopify; shoppers hitting Checkout land straight in Shopify's cart. Re-sync after any price/photo change." />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/shopify/push/${editing.id}`);
                          setEditing({ ...editing, shopify_variant_id: data.shopify_variant_id });
                          toast.success("Synced to Shopify — checkout link is live");
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Shopify sync failed");
                        }
                      }}
                      className="text-xs bg-purple-700 text-white font-bold px-3 py-1.5 rounded-full"
                      data-testid="shopify-sync-btn"
                    >
                      {editing.shopify_variant_id ? "Re-sync to Shopify" : "Sync to Shopify"}
                    </button>
                    <span className="text-[11px] text-purple-800">
                      {editing.shopify_variant_id ? "✓ Connected — checkout via Shopify" : "Not synced yet — checkout button won't work until synced"}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
              <button onClick={onSave} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="form-save-btn">
                {saving ? "Saving…" : editing.id ? "Save changes" : "Create product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
